import { refreshToken } from '@/lib/api/authService';
import storage from '@/utils/storage';

/**
 * `IApiErrorData<T>`
 *
 * A utility type for representing potential validation errors for a form defined by the structure `T`.
 *
 * The type mirrors the structure of `T`. However, every leaf node (final field) is an array of strings,
 * where each string represents a distinct error message for that field.
 *
 * @template T The type that represents the structure of the form data.
 *
 * @example
 * ```typescript
 * interface User {
 *     firstName: string;
 *     profile: {
 *         city: string;
 *     };
 * }
 *
 * const errors: IApiErrorData<User> = {
 *     firstName: [{message: "First name is required", code: 'invalid'}],
 *     profile: {
 *         city: [{message: "City is required", code: 'invalid'}],
 *     }
 * };
 * ```
 */
export type IApiErrorData<T> = {
  [P in keyof T]: T[P] extends object
    ? IApiErrorData<T[P]>
    : { errors: string[] } | { code: string; message: string }[];
};

export class ApiError<T> extends Error {
  constructor(
    public message: string,
    // eslint-disable-next-line no-unused-vars
    public errorObject: T | Record<string, IApiErrorData<T>>
  ) {
    super(message);
    // This line is necessary for the error stack trace to work correctly
    Object.setPrototypeOf(this, new.target.prototype);
  }
}
interface RequestConfig {
  params?: any;
  public?: boolean;
  customHeaders?: any;
  shouldNotRedirect?: boolean;
  customOrganizationid?: string;
  admin?: boolean;
}
const getUrlEncodedParams = (configParams: any = {}): string => {
  const params: string[] = [];
  if (!params) {
    return '';
  }
  for (const p in configParams) {
    const key = p;

    // Do not include it in query string params sent to APIs.
    if (key === 'countryId' && configParams[p] === 'other') {
      continue;
    }
    if (Array.isArray(configParams[p])) {
      configParams[p].forEach((element: any) => {
        params.push(`${key}=${encodeURIComponent(element)}`);
      });
    } else {
      params.push(`${key}=${encodeURIComponent(configParams[p])}`);
    }
  }
  if (params.length) {
    return `?${params.join('&')}`;
  }
  return '';
};

type HeadersInit = [string, string][] | Record<string, string> | Headers;

class ApiClient {
  // private router = useRouter();
  // private router: ReturnType<typeof useRouter>;
  // constructor() {
  //   this.router = useRouter();
  // }
  // This is for API Calls which need to send json data.
  // Authorised is the variable which will add authorization header to the request
  private getHeaders(
    authorised: boolean,
    customHeaders = {},
    customOrganizationid?: string
  ): HeadersInit {
    const _headers: HeadersInit = {
      'Content-Type': 'application/json',
      organizationid: customOrganizationid
        ? customOrganizationid
        : process.env.NEXT_PUBLIC_PWLIVE_ORG_ID || '',
      ...customHeaders,
    };
    const token: any = storage.get('TOKEN') || '';
    if (token && token?.length > 0 && authorised) {
      _headers['Authorization'] = `Bearer ${storage.get('TOKEN')}`;
    }
    return _headers;
  }
  /**
   * Makes an asynchronous GET request to a given URL path.
   *
   * @template T - The expected type of the response payload.
   * @template TError - The expected type of the error response.
   *
   * @param urlPath - The endpoint path to make the GET request to.
   * @param config - Configuration for the request, including parameters and other settings.
   *
   * @returns Promise<T> - Returns a promise that resolves with the response payload of type T.
   *
   * @throws ApiError<TError> - Throws an API error with details if the request fails.
   */
  async get<T, TError>(urlPath: string, config: RequestConfig): Promise<T> {
    // Construct the full URL using base URL, provided path, and encoded parameters.
    const response = await fetch(`${urlPath}${getUrlEncodedParams(config.params)}`, {
      method: 'GET',
      // Conditionally set headers based on whether the request is public or not.
      headers: this.getHeaders(!config.public, config.customHeaders),
    });
    // Check if the response status is 401 (Unauthorized)
    if (response.status === 401) {
      await refreshToken(config?.shouldNotRedirect);
    }

    // Check if the response was successful.
    if (!response.ok) {
      // If there's a body in the response, extract error details and throw them.
      if (response.body) {
        const d = await response.json();
        throw new ApiError<TError>(
          d?.message || d?.error?.message || 'Request failed with status',
          d?.errors
        );
      } else {
        // If there's no body, throw a generic error with the response status.
        throw new ApiError(`Request failed with status ${response.status}`, {});
      }
    }

    // Parse and return the JSON response.
    return response.json();
  }

  /**
   * This method sends a POST request.
   * @template T The expected return type of the request.
   * @template TError The expected error structure if the request fails.
   *
   * @returns {Promise<T>} A promise that resolves with the parsed JSON response.
   *
   * @throws {ApiError<TError>} If the response is not ok (status code is not in the 200-299 range), an `ApiError` will be thrown.
   * If the response has a body, the `errors` field of the response will be attached to the `ApiError`.
   * Otherwise, a generic message with the status code will be included in the `ApiError`.
   **/
  async post<T, TError>(urlPath: string, body: any, config: RequestConfig = {}): Promise<T> {
    const response = await fetch(`${urlPath}`, {
      method: 'POST',
      headers: this.getHeaders(!config.public, config?.customHeaders, config?.customOrganizationid),
      body: JSON.stringify(body),
    });
    // Check if the response status is 401 (Unauthorized)
    if (response.status === 401) {
      await refreshToken(config?.shouldNotRedirect);
    }
    if (!response.ok) {
      if (response.body) {
        const d = await response.json();
        throw new ApiError<TError>(
          d?.error?.message || d?.message || 'Request failed with status',
          d?.errors
        );
      } else {
        throw new ApiError(`Request failed with status ${response.status}`, {});
      }
    }
    return response.json();
  }

  /**
   * This method sends a PATCH request.
   * @template T The expected return type of the request.
   * @template TError The expected error structure if the request fails.
   *
   * @returns {Promise<T>} A promise that resolves with the parsed JSON response.
   *
   * @throws {ApiError<TError>} If the response is not ok (status code is not in the 200-299 range), an `ApiError` will be thrown.
   * If the response has a body, the `errors` field of the response will be attached to the `ApiError`.
   * Otherwise, a generic message with the status code will be included in the `ApiError`.
   **/
  async put<T, TError>(urlPath: string, body: any, config: RequestConfig = {}): Promise<T> {
    const response = await fetch(`${urlPath}`, {
      method: 'PUT',
      headers: this.getHeaders(!config.public, config?.customHeaders),
      body: JSON.stringify(body),
    });
    // Check if the response status is 401 (Unauthorized)
    if (response.status === 401) {
      await refreshToken(config?.shouldNotRedirect);
    }
    if (!response.ok) {
      if (response.body) {
        const d = await response.json();
        throw new ApiError<TError>('Request failed with status', d?.errors);
      } else {
        throw new ApiError(`Request failed with status ${response.status}`, {});
      }
    }
    return response.json();
  }

  async delete<T, TError>(urlPath: string, config: RequestConfig = {}): Promise<T> {
    const response = await fetch(`${urlPath}`, {
      method: 'DELETE',
      headers: this.getHeaders(!config.public),
    });
    // Check if the response status is 401 (Unauthorized)
    if (response.status === 401) {
      await refreshToken(config?.shouldNotRedirect);
    }

    if (!response.ok) {
      if (response.body) {
        const errorData = await response.json();
        throw new ApiError<TError>('Request failed with status', errorData?.errors);
      } else {
        throw new ApiError(`Request failed with status ${response.status}`, {});
      }
    }

    // For DELETE request, there might not be a response body
    // So, return an empty object as there is no JSON to parse
    return {} as T;
  }
  // private handleLogoutAction = () => {
  //   localStorage.clear();
  //   // deleteAllCookies();
  //   // setTimeout(() => {
  //     this.router.push('/');
  //   // });
  //   console.log('Logout action executed');
  // };
}

// async delete<T, TError>(
//   urlPath: string,
//   body: any,
//   config: RequestConfig = {}
// ): Promise<T> {
//   const response = await fetch(`${urlPath}`, {
//     method: 'DELETE',
//     headers: this.getHeaders(!config.public),
//     body: JSON.stringify(body),
//   });
//   if (!response.ok) {
//     if (response.body) {
//       const d = await response.json();
//       throw new ApiError<TError>('Request failed with status', d?.errors);
//     } else {
//       throw new ApiError(`Request failed with status ${response.status}`, {});
//     }
//   }
//   return response.json();
// }
// }

// const deleteAllCookies = () => {
//   const cookies = document.cookie.split(';');

//   for (let i = 0; i < cookies.length; i++) {
//     let cookie = cookies[i];
//     let eqPos = cookie.indexOf('=');
//     let name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
//     document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
//   }
// }

const apiClient = new ApiClient();

export default apiClient;
